import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleCheck } from 'lucide-react';
import contactInfo from '../../configs/contactInfo';
import { submitEnquiry } from '../../services/enquiryService';
import { getErrorMessage } from '../../utils/apiError';
import './enquiryForm.css';

// Shared by the Contact and Request-a-project forms: validation, the
// honeypot, submission to /api/enquiries and the "sent" state.

// "Monday–Friday, 9 AM–6 PM IST". A function: the hours come from Admin → Settings at runtime.
export const supportHours = () => {
  const { days, time } = contactInfo.businessHours;
  return `${days.replace(' - ', '–')}, ${time.replace(/:00/g, '').replace(' - ', '–')}`;
};

export const LIMITS = {
  name: 100,
  email: 200,
  phone: 20,
  subject: 150,
  message: 5000,
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^[0-9+()\-\s]+$/;

/** Checks the fields every enquiry has. Returns { field: message }. */
export const validateCommon = (values, { subjectRequired = false } = {}) => {
  const errors = {};
  const name = values.name.trim();
  const email = values.email.trim();
  const phone = values.phone.trim();
  const subject = values.subject.trim();
  const message = values.message.trim();

  if (!name) errors.name = 'Enter your name.';
  else if (name.length < 2) errors.name = 'Name must be at least 2 characters.';

  if (!email) errors.email = 'Enter your email address.';
  else if (!EMAIL_RE.test(email)) errors.email = 'Enter a valid email address.';

  if (phone && !PHONE_RE.test(phone))
    errors.phone = 'Enter a valid phone number.';

  if (subjectRequired && !subject) errors.subject = 'Add a subject.';

  if (!message) errors.message = 'Write your message.';
  else if (message.length < 10)
    errors.message = 'Add a little more detail (at least 10 characters).';

  return errors;
};

/**
 * Form state + submission. `validate(values)` returns { field: message };
 * `toPayload(values)` maps the form to the API body (without type/website).
 */
export const useEnquiryForm = ({ type, initialValues, validate, toPayload }) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent
  const [submitError, setSubmitError] = useState('');
  const formRef = useRef(null);
  const honeypotRef = useRef(null);

  const onChange = e => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
    setErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const onSubmit = async e => {
    e.preventDefault();
    if (status === 'sending') return;

    const found = validate(values);
    setErrors(found);
    const firstInvalid = Object.keys(found).find(key => found[key]);
    if (firstInvalid) {
      formRef.current?.querySelector(`[name="${firstInvalid}"]`)?.focus();
      return;
    }

    setStatus('sending');
    setSubmitError('');
    try {
      await submitEnquiry({
        type,
        ...toPayload(values),
        website: honeypotRef.current?.value || '',
      });
      setStatus('sent');
    } catch (error) {
      setSubmitError(
        getErrorMessage(error, "We couldn't send your message. Please try again.")
      );
      setStatus('idle');
    }
  };

  const reset = () => {
    setValues(initialValues);
    setErrors({});
    setSubmitError('');
    setStatus('idle');
  };

  // Props for an input/select/textarea bound to `name`.
  const field = (name, { hint = false } = {}) => ({
    id: `${type}-${name}`,
    name,
    value: values[name],
    onChange,
    'aria-invalid': errors[name] ? 'true' : undefined,
    'aria-describedby':
      [errors[name] && `${type}-${name}-error`, hint && `${type}-${name}-hint`]
        .filter(Boolean)
        .join(' ') || undefined,
  });

  return {
    values,
    errors,
    status,
    submitError,
    formRef,
    honeypotRef,
    onSubmit,
    reset,
    field,
  };
};

/** Label + control + error, using the kit's form classes. */
export const Field = ({ id, label, optional, error, hint, children }) => (
  <div className="pk-field">
    <label className="pk-label" htmlFor={id}>
      {label}
      {optional && <span className="pk-optional"> (optional)</span>}
    </label>
    {children}
    {hint && (
      <p className="pk-hint" id={`${id}-hint`}>
        {hint}
      </p>
    )}
    {error && (
      <p className="pk-error" id={`${id}-error`}>
        {error}
      </p>
    )}
  </div>
);

/**
 * Spam trap. Off-screen and skipped by keyboard and screen readers, so only
 * bots fill it in; the API then pretends to accept the message and drops it.
 */
export const Honeypot = ({ inputRef, idPrefix }) => (
  <div className="enq-hp" aria-hidden="true">
    <label htmlFor={`${idPrefix}-website`}>Website</label>
    <input
      ref={inputRef}
      id={`${idPrefix}-website`}
      type="text"
      name="website"
      tabIndex={-1}
      autoComplete="off"
      defaultValue=""
    />
  </div>
);

/** Shown in place of the form once the API has saved the enquiry. */
export const EnquirySent = ({ title, text, onReset, resetLabel }) => {
  const ref = useRef(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div className="enq-sent" ref={ref} tabIndex={-1} role="status">
      <span className="enq-sent__icon" aria-hidden="true">
        <CircleCheck />
      </span>
      <h2 className="pk-title-3">{title}</h2>
      <p className="pk-muted">{text}</p>
      <div className="pk-cluster enq-sent__actions">
        <button
          type="button"
          className="pk-btn pk-btn--secondary pk-btn--sm"
          onClick={onReset}
        >
          {resetLabel}
        </button>
        <Link className="pk-btn pk-btn--ghost pk-btn--sm" to="/products">
          Browse projects
        </Link>
      </div>
    </div>
  );
};
